import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from '../../../../../store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level, onMenuClick }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const customization = useSelector((state) => state.customization);
  const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
  const isSelected =
    customization.isOpen.findIndex((id) => id === item.id) > -1;

  const Icon = item.icon;
  // const itemIcon = item?.icon ? (
  //   <Icon stroke={1.5} size="1.3rem" />
  // ) : (
  //   <FiberManualRecordIcon
  //     sx={{
  //       width: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
  //       height: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6
  //     }}
  //     fontSize={level > 0 ? 'inherit' : 'medium'}
  //   />
  // );

  const itemIcon = item?.icon ? (
    <Icon
      stroke={1.5}
      size="1.3rem"
      style={{
        color: isSelected ? '#ffffff' : '#64748b',
        transition: '0.3s'
      }}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: isSelected ? 8 : 6,
        height: isSelected ? 8 : 6,
        color: isSelected ? '#ffffff' : '#64748b'
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = {
    component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch({ type: MENU_OPEN, id });

  //  close drawer for all screen sizes
  dispatch({ type: SET_MENU, opened: false });
  };

  // active menu item on page load
  useEffect(() => {
    const currentIndex = document.location.pathname
      .toString()
      .split('/')
      .findIndex((id) => id === item.id);
    if (currentIndex > -1) {
      dispatch({ type: MENU_OPEN, id: item.id });
    }
    // eslint-disable-next-line
  }, [pathname]);

  // Get translation key based on item title
  const getTranslationKey = (title) => {
    // Special case for "Create New"
    if (title === "Create New") {
      return "menu.register";
    }
    if (title === "State & District") {
      return "menu.stateDistrict";
    }
    if (title === "HP Frequency & Firmware") {
      return "menu.frequencyFirmware";
    }
    // Convert title to camelCase for menu.* keys
    const camelTitle = title
      .replace(/\s+/g, ' ')
      .split(' ')
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join('');

    return `menu.${camelTitle}`;
  };

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      sx={{
        borderRadius: `${customization.borderRadius}px`,
        mb: 0.5,
        alignItems: 'flex-start',
        backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
        py: level > 1 ? 1 : 1.25,
        pl: `${level * 24}px`,
        // ✅ default text color
        color: '#64748b',

        // 🔥 SELECTED ITEM STYLE
        '&.Mui-selected': {
          background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
          color: '#fff',
        },

        '&.Mui-selected:hover': {
          background: 'linear-gradient(135deg, #5a0fb5, #1e63d6)',
        },

        // hover effect
        '&:hover': {
          backgroundColor: 'rgba(106,17,203,0.08)',
        }
      }}
      selected={customization.isOpen.findIndex((id) => id === item.id) > -1}
      onClick={() => {
        itemHandler(item.id);
        onMenuClick?.();
      }}
    >
      <ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 36 }}>{itemIcon}</ListItemIcon>
      <ListItemText
        primary={
          <Typography variant={customization.isOpen.findIndex((id) => id === item.id) > -1 ? 'h5' : 'body1'} color="inherit">
            {/* Translate menu title if there is a matching key, fallback to the original title */}
            {t(getTranslationKey(item.title), { fallbackLng: 'en', defaultValue: item.title })}
          </Typography>
        }
        secondary={
          item.caption && (
            <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
              {item.caption}
            </Typography>
          )
        }
      />
      {item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
